<script setup lang="ts">
import { ref } from 'vue';
import ActionButton from '@/components/buttons/ActionButton.vue';
import { ALAvatar } from '@af-lib/vue-ui';
import { useALAvatar } from '@/features/avatar/useALAvatar';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useNavigation } from '@/hooks/useNavigation';
import { useSpaces } from '@/hooks/useSpaces';
import { accountNavItems, logout as logoutNavItem } from '@/router/navigation';
import { useGlobalStore } from '@/stores/global';

const globalStore = useGlobalStore();

const { logout } = globalStore;
const { auth } = useMeQuery();

const menuOpen = ref(false);

const { avatarUrl, firstName, lastName, gradients } = useALAvatar(auth, { imgSize: 'thumb' });

/**
 * we actually need to await useNavigation
 * the reason is still unknown, but without it we get vue warnings when switching languages
 */
const { availableItems } = await useNavigation(accountNavItems);
const { hasSpaces, redirectToSpaces } = useSpaces();
</script>

<template>
  <div class="d-flex justify-start align-center">
    <v-menu
      location="bottom end"
      activator="parent"
      v-model="menuOpen"
      :offset="9"
      transition="slide-y-transition"
    >
      <template #activator="{ props }">
        <div class="menu-btn d-flex justify-start align-center" v-bind="props">
          <v-btn class="text-none" icon elevation="0">
            <div class="d-flex justify-start align-center">
              <ALAvatar
                :avatar-url="avatarUrl"
                :firstName="firstName"
                :lastName="lastName"
                :gradients="gradients"
                size="m"
                variant="square"
              />
            </div>
          </v-btn>
        </div>
      </template>

      <v-list density="compact" nav class="pa-0 rounded-t-0 elevation-4" min-width="295px">
        <!-- SPACES BLOCK MENU-->
        <v-list-item v-if="hasSpaces" class="m-0 p-0">
          <div class="flex items-end justify-between gap-12 p-4">
            <p
              :class="`text-subtitle-3 font-weight-bold text-primary`"
              data-cy="candiate-spaces-menu-role"
            >
              {{ $t('general.role.personal') }}
            </p>
            <ActionButton
              cancel
              class="h-auto"
              data-cy="candidate-spaces-menu-btn"
              @click="redirectToSpaces"
            >
              {{ $t('navigation.go_to.space') }}
            </ActionButton>
          </div>
        </v-list-item>

        <v-divider v-if="hasSpaces"></v-divider>
        <!-- SPACES BLOCK MENU-->

        <v-list-item
          v-for="item in availableItems"
          :key="item.name"
          :value="item"
          color="contrast"
          :href="item.external ? item.route : undefined"
          :to="!item.external ? item.route : undefined"
          :target="item.targetBlank"
          class="text-body-3"
          height="48px"
        >
          <template v-slot:prepend>
            <v-icon :icon="item.icon" class="text-icon-3" color="iconThin"></v-icon>
          </template>

          {{ $t(item.label) }}
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item @click="logout(200)" class="text-body-3" height="48px">
          <template v-slot:prepend>
            <v-icon :icon="logoutNavItem.icon" class="text-icon-3" color="iconThin"></v-icon>
          </template>

          {{ $t(logoutNavItem.label) }}
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
.menu-btn {
  cursor: pointer;
}

/* Remove the width of the v-icon from vuetify => misaligned icon,thx vuetify :) */
:deep(.v-icon) {
  justify-content: unset;
  padding-inline-start: 0.6rem;
}
</style>
